import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import AdminDashboard from './pages/AdminDashboard';
import Customers     from './pages/Customers';
import Issues        from './pages/Issues';
import Devices       from './pages/Devices';
import Features      from './pages/Features';
import QATesting     from './pages/QATesting';
import PasswordResets from './pages/PasswordResets';
import AdminSettings from './pages/AdminSettings';
import './App.css';

// Auth gate — password validated against ADMIN_PASSWORD env var on the server
// via /api/admin-login. The password is then stored in localStorage and sent
// as a Bearer token with every /api/proxy/* call.

function Login({ onLogin }) {
  const [pw, setPw]     = useState('');
  const [err, setErr]   = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setErr('');
    setBusy(true);
    try {
      const r = await fetch('/api/admin-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: pw }),
      });
      if (r.ok) {
        localStorage.setItem('pg_admin_token', pw);
        onLogin();
      } else if (r.status === 401) {
        setErr('Wrong password');
      } else {
        const msg = await r.json().catch(() => ({}));
        setErr(msg.error || 'Login failed');
      }
    } catch (ex) {
      setErr('Network error');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#1e293b', padding: 'clamp(24px, 5vw, 40px)', borderRadius: 16, width: '100%', maxWidth: 360, margin: '0 16px', textAlign: 'center', boxShadow: '0 8px 40px rgba(0,0,0,0.4)' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🛡️</div>
        <h1 style={{ color: '#fff', fontSize: 22, fontWeight: 800, marginBottom: 4 }}>ParentsProtection</h1>
        <p style={{ color: '#64748b', fontSize: 13, marginBottom: 28 }}>Admin Dashboard</p>
        <form onSubmit={submit}>
          <input type="password" placeholder="Admin password" value={pw}
            onChange={e => setPw(e.target.value)}
            style={{ marginBottom: 14, background: '#0f172a', border: '1px solid #334155', color: '#fff' }} />
          {err && <div style={{ color: '#ef4444', fontSize: 12, marginBottom: 10 }}>{err}</div>}
          <button type="submit" className="btn" style={{ width: '100%', marginTop: 0 }}>Login →</button>
        </form>
      </div>
    </div>
  );
}

function Shell() {
  const [openIssues, setOpenIssues] = useState(0);
  const [pendingResets, setPendingResets] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    supabase.from('issues').select('*', { count: 'exact', head: true })
      .eq('status', 'open').then(({ count }) => setOpenIssues(count || 0));
    supabase.from('password_resets').select('*', { count: 'exact', head: true })
      .eq('status', 'pending').then(({ count }) => setPendingResets(count || 0))
      .catch(() => {}); // table may not exist yet
  }, []);

  const logout = () => { localStorage.removeItem('pg_admin_token'); window.location.reload(); };

  const nav = [
    { to: '/',          label: '📊 Dashboard'  },
    { to: '/customers', label: '👥 Customers'  },
    { to: '/password-resets', label: '🔑 Password Resets', badge: pendingResets },
    { to: '/issues',    label: '🎫 Issues', badge: openIssues },
    { to: '/devices',   label: '🖥️  Devices'   },
    { to: '/features',  label: '🎛️ Features'  },
    { to: '/qa',        label: '🧪 QA Testing' },
    { to: '/settings',  label: '⚙️ Settings'  },
  ];

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="sidebar-header">
          <div className="sidebar-brand">🛡️ ParentsProtection</div>
          <div className="sidebar-sub">Admin Panel · Private</div>
        </div>
        <nav>
          {nav.map(({ to, label, badge }) => (
            <NavLink key={to} to={to} end={to === '/'}
              className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
              {label}
              {badge > 0 && <span className="nav-badge">{badge}</span>}
            </NavLink>
          ))}
        </nav>
        <div style={{ padding: '8px 18px', borderTop: '1px solid #1e293b', marginTop: 'auto' }}>
          <a href="https://www.parentsprotection.org/dashboard" target="_blank" rel="noreferrer"
            style={{ display: 'block', padding: '8px 0', fontSize: 12, color: '#64748b', textDecoration: 'none' }}>
            ↗ Open Dashboard
          </a>
          <a href="https://www.parentsprotection.org" target="_blank" rel="noreferrer"
            style={{ display: 'block', padding: '8px 0', fontSize: 12, color: '#64748b', textDecoration: 'none' }}>
            ↗ View Website
          </a>
        </div>
        <div className="sidebar-footer">
          <div className="status-dot" />
          <span style={{ flex: 1 }}>Supabase connected</span>
          <button onClick={logout}
            style={{ border: 'none', background: 'none', color: '#475569', cursor: 'pointer', fontSize: 11 }}>
            Logout
          </button>
        </div>
      </aside>
      <main className="content">
        <Routes>
          <Route path="/"          element={<AdminDashboard />} />
          <Route path="/customers" element={<Customers />}      />
          <Route path="/password-resets" element={<PasswordResets />} />
          <Route path="/issues"    element={<Issues />}         />
          <Route path="/devices"   element={<Devices />}        />
          <Route path="/features"  element={<Features />}       />
          <Route path="/qa"        element={<QATesting />}     />
          <Route path="/settings"  element={<AdminSettings />} />
        </Routes>
      </main>
    </div>
  );
}

export default function App() {
  const [authed, setAuthed] = useState(!!localStorage.getItem('pg_admin_token'));
  if (!authed) return <Login onLogin={() => setAuthed(true)} />;
  return <Router><Shell /></Router>;
}
